import React, { useEffect, useRef, useState } from "react";
import { ChevronRight, MessageSquarePlus, Send } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styles from "./ChatbotPanel.module.css";
import { FaExpand } from "react-icons/fa6";
import SuggestionsPanel from "./SuggestionsPanel";

const ChatbotPanel = ({ messages = [], onSend, onClose, onNewChat, isStreaming = false }) => {
  const messagesRef = useRef(null);
  const inputRef = useRef(null);
  const [input, setInput] = useState("");
  const [expanded, setExpanded] = useState(false);
  const hasConversation = messages.some((m) => m.role === "user" || m.type === "chart");

  // show the welcome screen until user initiates chat or sends a message
  const [started, setStarted] = useState(() => {
    return hasConversation;
  });

  // static suggestions shown on the welcome screen
  const suggestionQuestions = [
    "How many patients were enrolled last month?",
    "What is the sum of billable amounts?",
    "List down all staff members who don't have time spent in last 24 hours.",
    "How many total RPM patients are registered?",
  ];


  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, started]);

  // focus input when chat starts
  useEffect(() => {
    if (started && inputRef.current) inputRef.current.focus();
  }, [started]);

  // if a user message arrives externally, switch out of welcome
  useEffect(() => {
    if (!started && hasConversation) {
      setStarted(true);
    }
  }, [hasConversation, started]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    onSend?.(trimmed);
    setInput("");
    setStarted(true);
  };

  const handleStartChat = () => {
    setStarted(true);
    // focus handled by effect
  };

  const handleNewChat = () => {
    setInput("");
    setStarted(true);
    onNewChat?.();
  };

  const handleSuggestionClick = (text) => {
    // start the chat and immediately send the chosen question
    setStarted(true);
    onSend?.(text);
  };

  const onKeyDown = (e) => {
    // Shift+Enter adds a newline, Enter sends the message
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <aside className={`${styles.panel} ${expanded ? styles.expanded : ""}`} aria-label="AI assistant">
      {/* collapse control at top-left (simple button) */}
      <button
        className={styles.collapseButton}
        type="button"
        aria-label="Collapse chat"
        onClick={onClose}
      >
        <ChevronRight size={20} strokeWidth={2.2} />
      </button>

      {/* header inside panel */}
      <header className={styles.panelHeader}>
        {started ? (
          <button
            className={styles.newChatButton}
            type="button"
            disabled={isStreaming}
            onClick={handleNewChat}
          >
            <MessageSquarePlus size={18} />
            {/* <span>New chat</span> */}
          </button>
        ) : (
          <div className={styles.headerPlaceholder} />
        )}

        <div className={styles.headerCenter}>
          <div className={`${styles.panelTitle} ${expanded ? styles.expandedTitle : ""}`}>AI Assistant</div>
          <div className={styles.panelSubtitle}>{expanded ? "Chat — fullscreen" : "Ask anything about toca health"}</div>
        </div>

        <button
          className={styles.expandButton}
          type="button"
          aria-pressed={expanded}
          aria-label={expanded ? "Exit fullscreen chat" : "Expand chat"}
          onClick={() => setExpanded((s) => !s)}
        >
          <FaExpand size={16} />
        </button>
      </header>

      <div className={styles.messages} ref={messagesRef}>
        {!started ? (
          <div className={styles.welcomeWrap}>
            <div className={styles.welcomeCard}>
              <div className={styles.welcomeIcon} aria-hidden>
                <img src="/assets/images/chat.png" alt="Chat" className={styles.welcomeImg} />
              </div>

              <div className={styles.welcomeActions}>
                <button className={styles.startButton} onClick={handleStartChat}>Start Chat</button>
              </div>
            </div>
          </div>
        ) : (
          <>
        {messages.length === 0 && (
          <SuggestionsPanel
            questions={suggestionQuestions}
            onSelect={handleSuggestionClick}
          />
        )}

            {messages
              .filter((m) => !(m.role === "ai" && !(m.text || "").trim()))
              .map((m) => (
                <div
                  key={m.id}
                  className={`${styles.message} ${m.role === "user" ? styles.userMessage : styles.aiMessage}`}
                >
                  <div className={styles.bubble}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{((m.text || "").trim())}</ReactMarkdown>
                  </div>
                </div>
              ))}

            {isStreaming && (
              <div className={`${styles.message} ${styles.aiMessage} ${styles.typingBubble}`}>
                <div className={styles.bubble}>
                  <div className={styles.typingDots} role="status" aria-live="polite" aria-label="AI is typing">
                    <span className={styles.dot} />
                    <span className={styles.dot} />
                    <span className={styles.dot} />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {started && (
        <div className={styles.inputArea}>
          <textarea
            ref={inputRef}
            className={styles.input}
            placeholder="Type your question here..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            rows={2}
          />

          <button
            className={styles.sendButton}
            type="button"
            onClick={handleSend}
            disabled={!input.trim() || isStreaming}
          >
            <Send size={16} strokeWidth={1.6} />
            <span className={styles.sendLabel}>{isStreaming ? "Sending..." : "Send"}</span>
          </button>
        </div>
      )}
    </aside>
  );
};

export default ChatbotPanel;
